import { useState } from "react";
import {
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";

import { GROUP_LABELS } from "./sectionMeta";

function SectionRow({
  section,
  isSectionOn,
  isPartShown,
  onToggle,
  onTogglePart,
}) {
  const [expanded, setExpanded] = useState(false);
  const hasParts = section.parts.length > 0;
  const shownCount = section.parts.filter((p) =>
    isPartShown(section.key, p.key)
  ).length;

  return (
    <Box>
      <Group justify="space-between" wrap="nowrap" gap="sm" px="sm" py={10}>
        <UnstyledButton
          onClick={() => setExpanded((v) => !v)}
          disabled={!hasParts}
          style={{
            flex: 1,
            minWidth: 0,
            cursor: hasParts ? "pointer" : "default",
          }}
        >
          <Group gap={6} wrap="nowrap">
            <IconChevronRight
              size={14}
              style={{
                flexShrink: 0,
                visibility: hasParts ? "visible" : "hidden",
                transform: expanded ? "rotate(90deg)" : undefined,
                transition: "transform 150ms ease",
                color: "var(--mantine-color-dimmed)",
              }}
            />
            <Text size="sm" fw={500} truncate>
              {section.label}
            </Text>
            {hasParts && shownCount < section.parts.length && (
              <Badge size="xs" variant="light" color="gray">
                {shownCount}/{section.parts.length}
              </Badge>
            )}
          </Group>
        </UnstyledButton>
        <Switch
          size="sm"
          checked={isSectionOn}
          onChange={(e) => onToggle(section.key, e.currentTarget.checked)}
        />
      </Group>

      {hasParts && (
        <Collapse expanded={expanded}>
          <Stack gap={4} pl={34} pr="sm" pb={10} pt={2}>
            {!isSectionOn && (
              <Text size="xs" c="dimmed">
                Turn the section on to change its parts.
              </Text>
            )}
            {section.parts.map((part) => (
              <Switch
                key={part.key}
                size="xs"
                labelPosition="left"
                label={part.label}
                checked={isPartShown(section.key, part.key)}
                disabled={!isSectionOn}
                onChange={(e) =>
                  onTogglePart(section.key, part.key, e.currentTarget.checked)
                }
                styles={{
                  body: { justifyContent: "space-between" },
                  labelWrapper: { flex: 1 },
                  label: { paddingInlineStart: 0 },
                }}
              />
            ))}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
}

function SectionSettingsDrawer({
  opened,
  onClose,
  groupedSections,
  enabledKeys,
  onToggle,
  isPartShown,
  onTogglePart,
  isAnalysisEnabled,
  isPatchingEnabled,
  onToggleAnalysisEnabled,
}) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title="Customize insights"
    >
      <Stack gap="lg">
        <Paper withBorder radius="md" p="sm">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                Show event analysis to wsAdmin
              </Text>
              <Text size="xs" c="dimmed">
                Makes the written analysis under each section visible to
                workspace admins.
              </Text>
            </Stack>
            <Switch
              size="sm"
              checked={isAnalysisEnabled}
              disabled={isPatchingEnabled}
              onChange={(e) => onToggleAnalysisEnabled(e.currentTarget.checked)}
            />
          </Group>
        </Paper>

        <Text size="xs" c="dimmed">
          Choose which sections and parts appear on the page and in the PDF
          export. Open a section to hide individual parts.
        </Text>

        {Object.entries(groupedSections).map(([group, items]) => (
          <Stack key={group} gap={6}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {GROUP_LABELS[group] ?? group}
            </Text>
            <Paper withBorder radius="md">
              {items.map((s, i) => (
                <Box key={s.key}>
                  {i > 0 && <Divider />}
                  <SectionRow
                    section={s}
                    isSectionOn={enabledKeys.has(s.key)}
                    isPartShown={isPartShown}
                    onToggle={onToggle}
                    onTogglePart={onTogglePart}
                  />
                </Box>
              ))}
            </Paper>
          </Stack>
        ))}
      </Stack>
    </Drawer>
  );
}

export default SectionSettingsDrawer;
